import React, { useState, useEffect } from 'react';
import apiService from '../utils/api';
import Sidebar from './sidebar/Sidebar';
import './Currency.css';
import Loader from './Loader';
import Snackbar from '@mui/material/Snackbar';
import Delete from '@mui/icons-material/Delete';
import Breadcrumbs from './Breadcrumbs';

const Currency = () => {
  const [currencies, setCurrencies] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Add modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCurrencyName, setNewCurrencyName] = useState('');
  const [newCurrencyCode, setNewCurrencyCode] = useState('');
  const [newCurrencySymbol, setNewCurrencySymbol] = useState('');

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCurrencyName, setEditCurrencyName] = useState('');
  const [editCurrencyCode, setEditCurrencyCode] = useState('');
  const [editCurrencySymbol, setEditCurrencySymbol] = useState('');

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteCurrencyId, setDeleteCurrencyId] = useState(null);

  // Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const fetchCurrencies = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.fetchData('currencies');
      const rawData = Array.isArray(response) ? response : (response?.data || []);
      const formatted = rawData.map((curr, index) => ({
        id: curr.id || curr.currency_id,
        name: curr.name || curr.CurrencyName || curr.value || '',
        code: curr.code || curr.currency_code || '',
        symbol: curr.symbol || '',
        displayIndex: index + 1
      }));
      setCurrencies(formatted);
      setFilteredCurrencies(formatted);
    } catch (err) {
      console.error('Error fetching currencies:', err);
      setError('Failed to load currencies. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = currencies.filter(curr =>
      curr.name.toLowerCase().includes(query) ||
      curr.code.toLowerCase().includes(query) ||
      (curr.symbol && curr.symbol.toLowerCase().includes(query))
    );
    setFilteredCurrencies(filtered);
  }, [searchQuery, currencies]);

  const handleAddCurrency = async () => {
    if (!newCurrencyName.trim()) {
      setSnackbarMessage('Currency name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }
    if (!newCurrencyCode.trim()) {
      setSnackbarMessage('Currency code is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        name: newCurrencyName.trim(),
        code: newCurrencyCode.trim().toUpperCase(),
        symbol: newCurrencySymbol.trim() || null,
        created_by: sessionStorage.getItem('user_id') || 'system',
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };

      const response = await apiService.postData('currencies', payload);
      if (response.status === 'success' || response.currency_id || response.id) {
        await fetchCurrencies();
        setNewCurrencyName('');
        setNewCurrencyCode('');
        setNewCurrencySymbol('');
        setIsModalOpen(false);
        setSnackbarMessage('Currency added successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to add currency');
      }
    } catch (err) {
      console.error('Error adding currency:', err);
      const errorMessage = err.response?.status === 409
        ? 'Currency with this code or name already exists'
        : (err.response?.data?.message || err.message || 'Failed to add currency. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editCurrencyName.trim()) {
      setSnackbarMessage('Currency name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }
    if (!editCurrencyCode.trim()) {
      setSnackbarMessage('Currency code is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const originalCurr = currencies.find(c => c.id === id);
      if (
        originalCurr &&
        originalCurr.name === editCurrencyName.trim() &&
        originalCurr.code === editCurrencyCode.trim().toUpperCase() &&
        (originalCurr.symbol || '') === editCurrencySymbol.trim()
      ) {
        setSnackbarMessage('No changes detected');
        setIsSuccess(false);
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      const payload = {
        name: editCurrencyName.trim(),
        code: editCurrencyCode.trim().toUpperCase(),
        symbol: editCurrencySymbol.trim() || null,
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };

      const response = await apiService.putData(`currencies/${id}`, payload);
      if (response.status === 'success' || response.id) {
        await fetchCurrencies();
        setSnackbarMessage('Currency updated successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
        setIsEditModalOpen(false);
        setEditingId(null);
        setEditCurrencyName('');
        setEditCurrencyCode('');
        setEditCurrencySymbol('');
      } else {
        throw new Error(response.message || 'Failed to update currency');
      }
    } catch (err) {
      console.error('Error updating currency:', err);
      const errorMessage = err.response?.status === 409
        ? 'Currency code or name already in use'
        : (err.response?.data?.message || err.message || 'Failed to update currency. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCurrency = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiService.deleteData(`currencies/${id}`);
      if (response.status === 'success' || response.currency_id) {
        await fetchCurrencies();
        setSnackbarMessage('Currency deleted successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to delete currency');
      }
    } catch (err) {
      console.error('Error deleting currency:', err);
      setSnackbarMessage(err.response?.data?.message || err.message || 'Failed to delete currency. Please try again later.');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteCurrencyId(null);
    }
  };

  return (
    <div className="currency-page-container">
      <Sidebar />
      <div className="currency-page-content">
        <Breadcrumbs />
        <div className="currency-header">
          <h2 style={{ color: '#ff6b35', fontSize: '24px' }}>Currency</h2>
          <button
            className="add-currency-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Currency
          </button>
        </div>

        {isLoading && currencies.length === 0 ? (
          <Loader isLoading={isLoading} message="Loading Currencies..." color="#ff6b35" />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search currencies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <svg
                className="currency-search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            <table className="currency-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Currency Name</th>
                  <th>Currency Code</th>
                  <th>Symbol</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCurrencies.length > 0 ? (
                  filteredCurrencies.map((curr, index) => (
                    <tr key={curr.id}>
                      <td>{index + 1}</td>
                      <td>{curr.name}</td>
                      <td><strong>{curr.code}</strong></td>
                      <td>{curr.symbol || '-'}</td>
                      <td>
                        <svg
                          className="edit-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ff6b35"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ cursor: 'pointer', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
                          title="Edit Currency"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setEditingId(curr.id);
                            setEditCurrencyName(curr.name);
                            setEditCurrencyCode(curr.code);
                            setEditCurrencySymbol(curr.symbol || '');
                          }}
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        <Delete
                          style={{
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                            color: '#ff6b35',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                          titleAccess="Delete Currency"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteCurrencyId(curr.id);
                            setIsDeleteModalOpen(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">No currencies found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {/* Add Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="currency-modal-content">
              <div className="currency-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Add Currency</h3>
              </div>
              <div className="currency-modal-form">
                <label htmlFor="currency-name" className="currency-label">Currency Name</label>
                <input
                  id="currency-name"
                  type="text"
                  value={newCurrencyName}
                  onChange={(e) => setNewCurrencyName(e.target.value)}
                  placeholder="e.g. US Dollar"
                  className="modal-input"
                />
                <label htmlFor="currency-code" className="currency-label" style={{ marginTop: '12px' }}>Currency Code</label>
                <input
                  id="currency-code"
                  type="text"
                  value={newCurrencyCode}
                  onChange={(e) => setNewCurrencyCode(e.target.value.toUpperCase())}
                  placeholder="e.g. USD"
                  maxLength="5"
                  className="modal-input"
                />
                <label htmlFor="currency-symbol" className="currency-label" style={{ marginTop: '12px' }}>Symbol (Optional)</label>
                <input
                  id="currency-symbol"
                  type="text"
                  value={newCurrencySymbol}
                  onChange={(e) => setNewCurrencySymbol(e.target.value)}
                  placeholder="e.g. $"
                  maxLength="5"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={handleAddCurrency}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setNewCurrencyName('');
                    setNewCurrencyCode('');
                    setNewCurrencySymbol('');
                    setIsModalOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && (
          <div className="modal-overlay">
            <div className="currency-modal-content">
              <div className="currency-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Update Currency</h3>
              </div>
              <div className="currency-modal-form">
                <label htmlFor="edit-currency-name" className="currency-label">Currency Name</label>
                <input
                  id="edit-currency-name"
                  type="text"
                  value={editCurrencyName}
                  onChange={(e) => setEditCurrencyName(e.target.value)}
                  placeholder="Enter currency name"
                  className="modal-input"
                />
                <label htmlFor="edit-currency-code" className="currency-label" style={{ marginTop: '12px' }}>Currency Code</label>
                <input
                  id="edit-currency-code"
                  type="text"
                  value={editCurrencyCode}
                  onChange={(e) => setEditCurrencyCode(e.target.value.toUpperCase())}
                  placeholder="Enter currency code"
                  maxLength="5"
                  className="modal-input"
                />
                <label htmlFor="edit-currency-symbol" className="currency-label" style={{ marginTop: '12px' }}>Symbol</label>
                <input
                  id="edit-currency-symbol"
                  type="text"
                  value={editCurrencySymbol}
                  onChange={(e) => setEditCurrencySymbol(e.target.value)}
                  placeholder="Enter symbol"
                  maxLength="5"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleSaveEdit(editingId)}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingId(null);
                    setEditCurrencyName('');
                    setEditCurrencyCode('');
                    setEditCurrencySymbol('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="currency-modal-content">
              <div className="currency-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Confirm Delete</h3>
              </div>
              <div className="currency-modal-form">
                <p>Do you want to delete this currency?</p>
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleDeleteCurrency(deleteCurrencyId)}
                >
                  Delete
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeleteCurrencyId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: isSuccess ? '#4caf50' : '#f44336',
            },
            zIndex: 1400,
          }}
        />
      </div>
    </div>
  );
};

export default Currency;
